import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ShipmentTable from "../../components/ShipmentTable";
import UpcomingShipment from "../../components/UpcomingShipment";
import TotalShipment from "../../components/TotalShipment";
import CompletedShipment from "../../components/CompletedShipment";
import BilledShipments from "../../components/BilledShipments";
import UnbilledReminderModal from "../../components/dashboard/UnbilledReminderModal";
import BillingNotificationsBell from "../../components/dashboard/BillingNotificationsBell";
import InactiveShipmentButton from "../../components/dashboard/InactiveShipmentButton";
import supabase from "../../supabaseClient";
import ProcessStepper from "../../components/ProcessStepper";
import MetricCard from "../../components/MetricCard";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import api from "../../config/api";
import {
  buildDashboardQueryParams,
  DASHBOARD_MONTH_OPTIONS,
} from "../../utils/dashboardDateFilter";
dayjs.extend(isoWeek);

const formatReminderDate = (value) => {
  if (!value) return "No date";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "No date" : parsed.toLocaleDateString();
};

const ReminderBoard = ({ reminders, onViewAllUnliquidated, onViewAllUnbilled }) => (
  <div className="rounded-2xl border border-amber-100 bg-white px-5 py-5 shadow-lg">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">
          Bulletin Board
        </p>
        <h2 className="text-2xl font-bold text-blue">Pending Reminders</h2>
        <p className="text-sm text-gray-600">
          Unbilled shipments and requests that are still not fully liquidated.
        </p>
      </div>
    </div>

    <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
      {[
        {
          key: "unbilled",
          title: "Unbilled Shipments",
          tone: "amber",
          empty: "No unbilled shipments found.",
        },
        {
          key: "unliquidated",
          title: "Unliquidated Requests",
          tone: "rose",
          empty: "No unliquidated requests found.",
        },
      ].map((section) => {
        const items = (reminders?.[section.key]?.items || []).slice(0, 6);
        const count = reminders?.[section.key]?.count || 0;
        const toneClass =
          section.tone === "rose"
            ? "border-rose-100 bg-rose-50 text-rose-700"
            : "border-amber-100 bg-amber-50 text-amber-700";

        return (
          <div key={section.key} className={`rounded-2xl border px-4 py-4 ${toneClass}`}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{section.title}</h3>
              <div className="flex items-center gap-2">
                {section.key === "unliquidated" ? (
                  <button
                    type="button"
                    onClick={onViewAllUnliquidated}
                    className="rounded-full border border-white/80 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-white"
                  >
                    View all
                  </button>
                ) : null}
                {section.key === "unbilled" ? (
                  <button
                    type="button"
                    onClick={onViewAllUnbilled}
                    className="rounded-full border border-white/80 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-white"
                  >
                    View all
                  </button>
                ) : null}
                <span className="rounded-full bg-white/80 px-3 py-1 text-sm font-bold text-slate-800">
                  {count}
                </span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {items.length ? (
                items.map((item) => (
                  <div key={`${section.key}-${item.id}`} className="rounded-xl border border-white/80 bg-white/80 px-3 py-3 text-sm shadow-sm">
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <div className="text-slate-600">{item.subtitle}</div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                      <span className="uppercase tracking-wide">{item.meta}</span>
                      <span>{formatReminderDate(item.date)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-white/80 bg-white/60 px-3 py-6 text-center text-sm text-slate-500">
                  {section.empty}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const userId = user?.user_id ?? user?.id;
  const currentYear = String(new Date().getFullYear());
  const yearOptions = Array.from({ length: 10 }, (_, i) =>
    String(new Date().getFullYear() - i)
  );
  const monthOptions = DASHBOARD_MONTH_OPTIONS;

  const [shipmentCount, setShipmentCount] = useState(0);
  const [upcomingShipmentsCount, setUpcomingShipmentsCount] = useState(0);
  const [activeShipments, setActiveShipmentsCount] = useState(0);
  const [completedShipments, setCompletedShipmentsCount] = useState(0);
  const [billedShipmentsCount, setBilledShipmentsCount] = useState(0);
  const [cancelledShipmentsCount, setCancelledShipmentsCount] = useState(0);

  const [selectedCard, setSelectedCard] = useState("active");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState("all");

  const [graphData, setGraphData] = useState([]);
  const [unbilledModalOpen, setUnbilledModalOpen] = useState(false);
  const [reminders, setReminders] = useState({
    unbilled: { count: 0, items: [] },
    unliquidated: { count: 0, items: [] },
  });

  const applySelectedDateToShipmentQuery = useCallback(
    (query) => {
      if (selectedYear === "all") {
        return query;
      }

      if (selectedMonth !== "all") {
        const nextYear = selectedMonth === "12" ? Number(selectedYear) + 1 : Number(selectedYear);
        const nextMonth = selectedMonth === "12" ? "01" : String(Number(selectedMonth) + 1).padStart(2, "0");

        return query
          .gte("date", `${selectedYear}-${selectedMonth}-01`)
          .lt("date", `${nextYear}-${nextMonth}-01`);
      }

      return query
        .gte("date", `${selectedYear}-01-01`)
        .lt("date", `${Number(selectedYear) + 1}-01-01`);
    },
    [selectedMonth, selectedYear]
  );

  useEffect(() => {
    if (selectedYear === "all" && selectedMonth !== "all") {
      setSelectedMonth("all");
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (userId) {
      // Initial fetch only - real-time updates handled by Supabase subscription
      const fetchData = async () => {
        try {
          const filterParams = buildDashboardQueryParams({
            year: selectedYear,
            month: selectedMonth,
          });
          const response = await api.get(
            `/api/dashboard/dashboard/${userId}${filterParams}`
          );
          const data = response.data;

          if (!data.error) {
            setShipmentCount(data.shipmentCount);
            setUpcomingShipmentsCount(data.upcomingShipmentsCount);
            setActiveShipmentsCount(data.activeShipmentsCount);
            setCompletedShipmentsCount(data.completedShipmentsCount);
          setGraphData(data.graphData);
            setReminders(
              data.reminders || {
                unbilled: { count: 0, items: [] },
                unliquidated: { count: 0, items: [] },
              }
            );
          }
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        }
      };

      fetchData();

      // Fetch billed shipments count
      const fetchBilledCount = async () => {
        try {
          const billedCountQuery = applySelectedDateToShipmentQuery(
            supabase
              .from("shipments")
              .select("*", { count: "exact", head: true })
              .eq("is_billed", true)
              .eq("user_id", userId)
          );

          const { count, error } = await billedCountQuery;

          if (!error) {
            setBilledShipmentsCount(count || 0);
          }
        } catch (error) {
          console.error("Error fetching billed shipments count:", error);
        }
      };

      const fetchCancelledCount = async () => {
        try {
          const cancelledCountQuery = applySelectedDateToShipmentQuery(
            supabase
              .from("shipments")
              .select("*", { count: "exact", head: true })
              .eq("status", "Cancelled")
              .eq("user_id", userId)
          );

          const { count, error } = await cancelledCountQuery;

          if (!error) {
            setCancelledShipmentsCount(count || 0);
          }
        } catch (error) {
          console.error("Error fetching cancelled shipments count:", error);
        }
      };

      fetchBilledCount();
      fetchCancelledCount();
    }
  }, [applySelectedDateToShipmentQuery, userId, selectedMonth, selectedYear]);

  useEffect(() => {
    if (!userId) return;

    const subscription = supabase
      .channel("shipments-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shipments" },
        async () => {
          try {
            const filterParams = buildDashboardQueryParams({
              year: selectedYear,
              month: selectedMonth,
            });
            const response = await api.get(
              `/api/dashboard/dashboard/${userId}${filterParams}`
            );
            const data = response.data;

            if (!data.error) {
              setShipmentCount(data.shipmentCount);
              setUpcomingShipmentsCount(data.upcomingShipmentsCount);
              setActiveShipmentsCount(data.activeShipmentsCount);
              setCompletedShipmentsCount(data.completedShipmentsCount);
              setGraphData(data.graphData);
              setReminders(
                data.reminders || {
                  unbilled: { count: 0, items: [] },
                  unliquidated: { count: 0, items: [] },
                }
              );
            }

            // Update billed shipments count
            const billedCountQuery = applySelectedDateToShipmentQuery(
              supabase
                .from("shipments")
                .select("*", { count: "exact", head: true })
                .eq("is_billed", true)
                .eq("user_id", userId)
            );

            const { count } = await billedCountQuery;

            setBilledShipmentsCount(count || 0);

            const cancelledCountQuery = applySelectedDateToShipmentQuery(
              supabase
                .from("shipments")
                .select("*", { count: "exact", head: true })
                .eq("status", "Cancelled")
                .eq("user_id", userId)
            );

            const { count: cancelledCount } = await cancelledCountQuery;

            setCancelledShipmentsCount(cancelledCount || 0);
          } catch (error) {
            console.error("Error updating dashboard data:", error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [applySelectedDateToShipmentQuery, userId, selectedMonth, selectedYear]);

  const [searchTerm] = useState("");
  const [selectedShipment, setSelectedShipment] = useState(null);

  useEffect(() => {
    setSelectedShipment(null);
  }, [selectedCard, selectedMonth, selectedYear]);

  // Create stable callback for row clicks
  const handleRowClick = useCallback((shipment) => {
    setSelectedShipment(shipment);
  }, []);

  // Get KPI status for waybill background color
  const getKPIStatus = (shipment) => {
    const shipmentType = shipment.shipment_type;

    if (shipmentType !== "AIR" && shipmentType !== "SEA" && shipmentType !== "BROKERAGE") {
      return null;
    }

    const arrivalRemark = shipment.remarks?.find(
      (r) => r.status === "Arrival at Destination"
    );

    if (!arrivalRemark || !arrivalRemark.aad) {
      return null;
    }

    let arrivalDate = dayjs(arrivalRemark.aad);
    if (!arrivalDate.isValid()) {
      return null;
    }

    let completionDate;
    if (shipment.status === "Delivered" || shipment.status === "Turnover") {
      const completionRemark = shipment.remarks?.find(
        (r) => r.status === "Delivered" || r.status === "Turnover"
      );
      completionDate = completionRemark?.date ? dayjs(completionRemark.date) : dayjs();
    } else {
      completionDate = dayjs();
    }

    const daysDiff = completionDate.diff(arrivalDate, "day");

    if (daysDiff < 0) {
      return { status: "On Time", bgColor: "bg-green-100" };
    }

    if (shipmentType === "AIR") {
      if (daysDiff <= 2) {
        return { status: "On Time", bgColor: "bg-green-100" };
      } else {
        return { status: "Delayed", bgColor: "bg-red-100" };
      }
    }

    if (shipmentType === "SEA" || shipmentType === "BROKERAGE") {
      if (daysDiff <= 5) {
        return { status: "On Time", bgColor: "bg-green-100" };
      } else if (daysDiff <= 10) {
        return { status: "Slightly Delayed", bgColor: "bg-orange-200" };
      } else {
        return { status: "Delayed", bgColor: "bg-red-100" };
      }
    }

    return null;
  };

  const CustomLegend = (value) => (
    <span style={{ marginRight: "25px", fontSize: "14px" }}>{value}</span>
  );

  // Placeholder matches the sleek theme but does not change behavior
  const ProcessStepperPlaceholder = () => (
    <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 w-full h-full shadow-lg border border-gray-100 flex flex-col items-center justify-center">
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        <div className="absolute top-4 right-4 w-16 h-16 bg-blue/5 rounded-full blur-xl opacity-60 animate-pulse"></div>
        <div className="absolute bottom-6 left-6 w-12 h-12 bg-yellow/5 rounded-full blur-lg opacity-40 animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 text-center space-y-4 max-w-sm">
        <div className="relative mx-auto w-16 h-16">
          <div className="absolute inset-0 bg-gradient-to-br from-blue/10 to-blue/20 rounded-full animate-pulse"></div>
          <div className="absolute inset-2 bg-gradient-to-br from-blue to-blue/90 rounded-full flex items-center justify-center shadow-lg">
            <i className="fas fa-route text-white text-xl"></i>
          </div>
          <div className="absolute -inset-2 border-2 border-blue/20 rounded-full animate-ping opacity-30"></div>
        </div>

        <h3 className="text-lg font-bold text-blue">Process Tracker</h3>
        <p className="text-grey text-sm font-medium">
          Select a shipment to view the detailed process timeline
        </p>

        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center justify-center gap-2 text-blue text-sm font-medium">
            <i className="fas fa-mouse-pointer text-xs animate-bounce"></i>
            <span>Click any shipment from the table</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue/10 to-transparent rounded-b-2xl"></div>
    </div>
  );

  return (
    <section id="dashboard" className="flex flex-col justify-center font-poppins min-h-screen">
      <div className="flex flex-col bg-gray-100 justify-center p-[5px] gap-[30px]">
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white px-5 py-5 shadow-lg sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
              Dashboard Overview
            </p>
            <h1 className="text-2xl font-bold text-blue sm:text-3xl">
              Admin Dashboard
            </h1>
          </div>
          <div className="w-full sm:w-auto">
            <label
              htmlFor="admin-dashboard-year-filter"
              className="mb-2 block text-sm font-semibold text-gray-700"
            >
              Filter by Period
            </label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <select
                  id="admin-dashboard-year-filter"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full appearance-none rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-11 text-blue font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-blue/30 sm:min-w-[220px]"
                >
                  <option value="all">All Years</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-blue">
                  <i className="fas fa-chevron-down text-sm" />
                </div>
              </div>
              <div className="relative">
                <select
                  id="admin-dashboard-month-filter"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={selectedYear === "all"}
                  className="w-full appearance-none rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-11 text-blue font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-blue/30 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 sm:min-w-[220px]"
                >
                  {monthOptions.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-blue">
                  <i className="fas fa-chevron-down text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <InactiveShipmentButton
                  user={user}
                  shipmentsPath="/admin/shipment-management"
                />
                <BillingNotificationsBell
                  user={user}
                  billingListPath="/admin/billing-list"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards (revamped theme, same behavior) */}
        {useMemo(() => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-[40px] md:gap-[20px] w-full">
            <MetricCard
              icon="fas fa-shipping-fast"
              count={activeShipments}
              title="Active Shipments"
              cardType="active"
              selectedCard={selectedCard}
              onClick={() => setSelectedCard("active")}
            />
            <MetricCard
              icon="fa-solid fa-hourglass-half"
              count={upcomingShipmentsCount}
              title="Shipment Nomination"
              cardType="upcoming"
              selectedCard={selectedCard}
              onClick={() => setSelectedCard("upcoming")}
            />
            <MetricCard
              icon="fa-solid fa-chart-simple"
              count={shipmentCount}
              title="Total Shipments"
              cardType="total"
              selectedCard={selectedCard}
              onClick={() => setSelectedCard("total")}
            />
            <MetricCard
              icon="fas fa-circle-check"
              count={completedShipments}
              title="Delivered Shipments"
              cardType="completed"
              selectedCard={selectedCard}
              onClick={() => setSelectedCard("completed")}
            />
            <MetricCard
              icon="fas fa-ban"
              count={cancelledShipmentsCount}
              title="Cancelled Shipments"
              cardType="cancelled"
              selectedCard={selectedCard}
              onClick={() => setSelectedCard("cancelled")}
            />
            <MetricCard
              icon="fas fa-receipt"
              count={`${billedShipmentsCount} / ${shipmentCount}`}
              title="Billed Shipments"
              cardType="billed"
              selectedCard={selectedCard}
              onClick={() => setSelectedCard("billed")}
            />
          </div>
        ), [selectedCard, activeShipments, upcomingShipmentsCount, shipmentCount, completedShipments, cancelledShipmentsCount, billedShipmentsCount])}

        <ReminderBoard
          reminders={reminders}
          onViewAllUnliquidated={() => navigate("/admin/liquidation")}
          onViewAllUnbilled={() => setUnbilledModalOpen(true)}
        />

        <UnbilledReminderModal
          isOpen={unbilledModalOpen}
          onClose={() => setUnbilledModalOpen(false)}
          shipments={reminders?.unbilled?.items || []}
          user={user}
          onUploaded={() => {
            setUnbilledModalOpen(false);
            const filterParams = buildDashboardQueryParams({
              year: selectedYear,
              month: selectedMonth,
            });
            api.get(`/api/dashboard/dashboard/${userId}${filterParams}`).then((response) => {
              const data = response.data;
              if (!data.error) {
                setShipmentCount(data.shipmentCount);
                setUpcomingShipmentsCount(data.upcomingShipmentsCount);
                setActiveShipmentsCount(data.activeShipmentsCount);
                setCompletedShipmentsCount(data.completedShipmentsCount);
                setGraphData(data.graphData);
                setReminders(
                  data.reminders || {
                    unbilled: { count: 0, items: [] },
                    unliquidated: { count: 0, items: [] },
                  }
                );
              }
            });
          }}
        />

        {/* Active / Upcoming / Total / Completed + Stepper */}
        {useMemo(() => (
          <div className="active-shipment-container grid grid-cols-1 lg:grid-cols-5 grid-rows-1 gap-4">
            <div className="active-table col-span-1 lg:col-span-3 row-span-1 p-3 bg-white shadow-lg rounded-2xl border border-gray-100">
              <div className="active-table-container">
                {selectedCard === "active" && (
                  <ShipmentTable
                    searchTerm={searchTerm}
                    onRowClick={handleRowClick}
                    role={"employee"}
                    filterValue={"Owned"}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                  />
                )}
                {selectedCard === "upcoming" && (
                  <UpcomingShipment
                    searchTerm={searchTerm}
                    onRowClick={handleRowClick}
                    role={"employee"}
                    filterValue={"Owned"}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                  />
                )}
                {selectedCard === "total" && (
                  <TotalShipment
                    searchTerm={searchTerm}
                    onRowClick={handleRowClick}
                    role={"employee"}
                    filterValue={"Owned"}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                  />
                )}
                {selectedCard === "cancelled" && (
                  <TotalShipment
                    searchTerm={searchTerm}
                    onRowClick={handleRowClick}
                    role={"employee"}
                    filterValue={"Owned"}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    statusFilter={"cancelled"}
                    title={"Cancelled Shipments"}
                  />
                )}
                {selectedCard === "completed" && (
                  <CompletedShipment
                    searchTerm={searchTerm}
                    onRowClick={handleRowClick}
                    role={"employee"}
                    filterValue={"Owned"}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                  />
                )}
                {selectedCard === "billed" && (
                  <BilledShipments
                    onRowClick={handleRowClick}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                  />
                )}
              </div>
            </div>

            <div className="stepper-container col-span-1 lg:col-start-4 lg:col-end-6 row-span-1">
              <div className="w-full h-full">
                {selectedShipment ? (
                  <div className="bg-white w-full h-full shadow-lg p-4 rounded-2xl border border-gray-100">
                    <ProcessStepper
                      shipment={selectedShipment}
                      kpiColor={user?.role === "Corporate" ? null : getKPIStatus(selectedShipment)?.bgColor}
                    />
                  </div>
                ) : (
                  <ProcessStepperPlaceholder />
                )}
              </div>
            </div>
          </div>
        ), [handleRowClick, selectedCard, searchTerm, selectedShipment, selectedMonth, selectedYear, user?.role])}

        {/* Shipment Summary Chart */}
        <div className="flex flex-col w-full">
          <div className="relative bg-white/90 backdrop-blur-sm p-6 sm:p-8 shadow-lg w-full rounded-2xl border border-gray-100">
            <div className="absolute top-5 right-6 w-16 h-16 bg-blue/5 rounded-full blur-xl opacity-60 animate-pulse pointer-events-none"></div>
            <h2 className="text-2xl sm:text-3xl font-bold text-blue text-center mb-2">
              Shipment Summary
            </h2>
            <p className="text-center text-sm text-grey mb-6">
              (Click on any bar to view drill down details.)
            </p>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={graphData} margin={{ right: 30 }} barCategoryGap={20} barGap={10}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timeframe" />
                <YAxis type="number" />
                <Tooltip />
                <Legend formatter={CustomLegend} />
                <Bar dataKey="total" fill="#0070C0" name="Total Shipments" barSize={20} />
                <Bar dataKey="upcoming" fill="#D6D33C" name="Nominated Shipments" barSize={20} />
                <Bar dataKey="active" fill="#606060" name="Active Shipments" barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

       
      </div>
    </section>
  );
};

export default Dashboard;
